<template>
  <div class="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
    <datalist id="onboarding-category-suggestions">
      <option v-for="c in categorySuggestions" :key="c" :value="c" />
    </datalist>
    <!-- Main services header -->
    <div class="px-4 py-3 bg-gradient-to-r from-pink-50 to-purple-50 border-b border-slate-100">
      <div class="flex items-center gap-2">
        <Scissors class="w-4 h-4 text-pink-600" />
        <span class="font-bold text-sm text-slate-800">שירותים ראשיים</span>
        <span class="text-xs text-slate-400 mr-auto">{{ mainEnabledCount }}/{{ mainServices.length }}</span>
      </div>
    </div>
    <div class="divide-y divide-slate-100">
      <div
        v-for="service in mainServices"
        :key="service.tempId"
      >
        <!-- Service row -->
        <div
          class="flex items-center gap-3 px-4 py-3 transition-colors"
          :class="service.enabled ? '' : 'opacity-50'"
        >
          <div class="w-10 h-10 rounded-lg overflow-hidden bg-slate-100 flex-shrink-0 flex items-center justify-center">
            <img
              v-if="service.imageUrl"
              :src="service.imageUrl"
              :alt="service.name"
              class="w-full h-full object-cover"
              loading="lazy"
            />
            <Scissors v-else class="w-4 h-4 text-slate-300" />
          </div>
          <div class="flex-1 min-w-0">
            <span class="text-sm font-medium text-slate-800 truncate block">{{ service.name }}</span>
            <p v-if="service.description" class="text-[11px] text-slate-400 truncate mt-0.5">{{ service.description }}</p>
            <div class="flex items-center gap-3 mt-1 text-xs text-slate-400">
              <span class="flex items-center gap-1">
                <Clock class="w-3 h-3" />
                {{ service.duration }} דק׳
              </span>
              <span class="flex items-center gap-1">
                <span>₪</span>
                {{ priceDisplay(service) }}
              </span>
              <span v-if="service.category" class="flex items-center gap-1 text-purple-500">
                <Tag class="w-3 h-3" />
                {{ service.category }}
              </span>
            </div>
          </div>
          <button
            class="w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:text-purple-600 hover:bg-purple-50 transition-colors flex-shrink-0"
            @click="toggleEdit(service.tempId)"
          >
            <Pencil class="w-3.5 h-3.5" />
          </button>
          <InputSwitch
            :modelValue="service.enabled"
            @update:modelValue="$emit('toggle-service', service.tempId)"
            :pt="{
              root: { class: 'w-10 h-5' },
              slider: { class: 'before:w-4 before:h-4' },
            }"
          />
        </div>

        <!-- Inline edit form -->
        <div v-if="editingId === service.tempId" class="px-4 pb-4 bg-slate-50 border-t border-slate-100">
          <div class="flex flex-col gap-2 pt-3">
            <input
              v-model="editForm.name"
              type="text"
              class="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-900 focus:outline-none focus:border-purple-400"
              placeholder="שם השירות"
            />
            <div class="flex gap-2">
              <div class="flex-1">
                <label class="text-[10px] text-slate-400 font-medium mb-1 block">משך (דקות)</label>
                <input
                  v-model.number="editForm.duration"
                  type="number"
                  min="5"
                  step="5"
                  class="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-900 focus:outline-none focus:border-purple-400"
                />
              </div>
              <div class="flex-1">
                <label class="text-[10px] text-slate-400 font-medium mb-1 block">מחיר (₪)</label>
                <input
                  v-model.number="editForm.price"
                  type="number"
                  min="0"
                  step="10"
                  class="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-900 focus:outline-none focus:border-purple-400"
                />
              </div>
            </div>
            <div class="flex gap-2">
              <button
                class="flex-1 bg-purple-600 hover:bg-purple-500 text-white text-xs font-medium py-2 rounded-lg transition-colors"
                @click="saveEdit(service.tempId)"
              >
                שמור
              </button>
              <button
                class="px-4 text-slate-500 text-xs font-medium py-2 rounded-lg border border-slate-200 hover:bg-white transition-colors"
                @click="editingId = null"
              >
                ביטול
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Complementary services section -->
    <template v-if="complementaryServices.length > 0">
      <div class="px-4 py-3 bg-gradient-to-r from-amber-50 to-orange-50 border-t border-b border-slate-100">
        <div class="flex items-center gap-2">
          <Plus class="w-4 h-4 text-amber-600" />
          <span class="font-bold text-sm text-slate-800">שירותים משלימים</span>
          <span class="text-xs text-slate-400 mr-auto">{{ compEnabledCount }}/{{ complementaryServices.length }}</span>
        </div>
        <p class="text-[11px] text-slate-400 mt-0.5">תוספות שהלקוח יכול לבחור יחד עם שירות ראשי</p>
      </div>
      <div class="divide-y divide-slate-100">
        <div
          v-for="service in complementaryServices"
          :key="service.tempId"
        >
          <div
            class="flex items-center gap-3 px-4 py-3 transition-colors"
            :class="service.enabled ? '' : 'opacity-50'"
          >
            <div class="w-10 h-10 rounded-lg overflow-hidden bg-slate-100 flex-shrink-0 flex items-center justify-center">
              <img
                v-if="service.imageUrl"
                :src="service.imageUrl"
                :alt="service.name"
                class="w-full h-full object-cover"
                loading="lazy"
              />
              <Plus v-else class="w-4 h-4 text-slate-300" />
            </div>
            <div class="flex-1 min-w-0">
              <span class="text-sm font-medium text-slate-800 truncate block">{{ service.name }}</span>
              <p v-if="service.description" class="text-[11px] text-slate-400 truncate mt-0.5">{{ service.description }}</p>
              <div class="flex items-center gap-3 mt-1 text-xs text-slate-400">
                <span class="flex items-center gap-1">
                  <Clock class="w-3 h-3" />
                  {{ service.duration }} דק׳
                </span>
                <span class="flex items-center gap-1">
                  <span>₪</span>
                  {{ service.price }}
                </span>
              </div>
              <p v-if="getAttachedNames(service).length" class="text-[11px] text-purple-500 mt-1">
                משלים: {{ getAttachedNames(service).join(', ') }}
              </p>
            </div>
            <button
              class="w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:text-purple-600 hover:bg-purple-50 transition-colors flex-shrink-0"
              @click="toggleEdit(service.tempId)"
            >
              <Pencil class="w-3.5 h-3.5" />
            </button>
            <InputSwitch
              :modelValue="service.enabled"
              @update:modelValue="$emit('toggle-service', service.tempId)"
              :pt="{
                root: { class: 'w-10 h-5' },
                slider: { class: 'before:w-4 before:h-4' },
              }"
            />
          </div>

          <!-- Inline edit form for complementary -->
          <div v-if="editingId === service.tempId" class="px-4 pb-4 bg-slate-50 border-t border-slate-100">
            <div class="flex flex-col gap-2 pt-3">
              <input
                v-model="editForm.name"
                type="text"
                class="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-900 focus:outline-none focus:border-purple-400"
                placeholder="שם השירות"
              />
              <div class="flex gap-2">
                <div class="flex-1">
                  <label class="text-[10px] text-slate-400 font-medium mb-1 block">משך (דקות)</label>
                  <input
                    v-model.number="editForm.duration"
                    type="number"
                    min="5"
                    step="5"
                    class="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-900 focus:outline-none focus:border-purple-400"
                  />
                </div>
                <div class="flex-1">
                  <label class="text-[10px] text-slate-400 font-medium mb-1 block">מחיר (₪)</label>
                  <input
                    v-model.number="editForm.price"
                    type="number"
                    min="0"
                    step="10"
                    class="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-900 focus:outline-none focus:border-purple-400"
                  />
                </div>
              </div>
              <div class="flex flex-col">
                <label class="text-[10px] text-slate-400 font-medium mb-1 block">קטגוריה</label>
                <input
                  v-model="editForm.category"
                  type="text"
                  list="onboarding-category-suggestions"
                  placeholder="למשל: שיער, ציפורניים"
                  class="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-900 focus:outline-none focus:border-purple-400"
                />
              </div>
              <div class="flex items-center gap-2">
                <div class="w-10 h-10 rounded-lg overflow-hidden bg-white border border-slate-200 flex-shrink-0 flex items-center justify-center">
                  <img v-if="service.imageUrl" :src="service.imageUrl" :alt="service.name" class="w-full h-full object-cover" />
                  <Scissors v-else class="w-4 h-4 text-slate-300" />
                </div>
                <button
                  class="flex-1 flex items-center justify-center gap-1.5 text-purple-600 text-xs font-medium py-2 rounded-lg border border-purple-200 hover:bg-purple-50 transition-colors"
                  @click="openImagePicker(service)"
                >
                  <Image class="w-3.5 h-3.5" />
                  תמונות מוצעות
                </button>
              </div>
              <div class="flex gap-2">
                <button
                  class="flex-1 bg-purple-600 hover:bg-purple-500 text-white text-xs font-medium py-2 rounded-lg transition-colors"
                  @click="saveEdit(service.tempId)"
                >
                  שמור
                </button>
                <button
                  class="px-4 text-slate-500 text-xs font-medium py-2 rounded-lg border border-slate-200 hover:bg-white transition-colors"
                  @click="editingId = null"
                >
                  ביטול
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, reactive } from 'vue';
import { IOnboardingSuggestedService, PriceTypeEnum, ServiceType } from '@torim-app/types';
import InputSwitch from 'primevue/inputswitch';
import { Scissors, Clock, Plus, Pencil, Image, Tag } from 'lucide-vue-next';
import { useDrawer } from '@torim-app/shared-components';
import ServiceImagePicker from '@app/components/admin/modals/ServiceImagePicker.vue';

const { openDrawer, closeDrawer } = useDrawer();

const props = defineProps<{
  services: IOnboardingSuggestedService[];
}>();

const emit = defineEmits<{
  'toggle-service': [tempId: string];
  'edit-services': [];
  'update-service': [tempId: string, changes: { name: string; duration: number; price: number; category?: string }];
  'set-image': [tempId: string, imageUrl: string];
}>();

const mainServices = computed(() =>
  props.services.filter(s => s.serviceType !== ServiceType.COMPLEMENTARY)
);

const complementaryServices = computed(() =>
  props.services.filter(s => s.serviceType === ServiceType.COMPLEMENTARY)
);

const mainEnabledCount = computed(() => mainServices.value.filter(s => s.enabled).length);
const compEnabledCount = computed(() => complementaryServices.value.filter(s => s.enabled).length);

const getAttachedNames = (service: IOnboardingSuggestedService): string[] => {
  if (!service.attachedToServices?.length) return [];
  const nameMap = new Map(props.services.map(s => [s.tempId, s.name]));
  return service.attachedToServices.map(id => nameMap.get(id)).filter(Boolean) as string[];
};

// How a service's price reads in the row: a range, a starting amount, or a single number.
const priceDisplay = (service: IOnboardingSuggestedService): string => {
  if (service.priceType === PriceTypeEnum.RANGE && service.maxPrice && service.maxPrice > service.price)
    return `${service.price}–${service.maxPrice}`;
  if (service.priceType === PriceTypeEnum.FROM)
    return `${service.price}+`;
  return String(service.price);
};

// Distinct categories already present in the proposal — fed to the <datalist> for suggestions.
const categorySuggestions = computed<string[]>(() => {
  const set = new Set<string>();
  for (const s of props.services)
    if (s.category) set.add(s.category.trim());
  return Array.from(set);
});

const editingId = ref<string | null>(null);
const editForm = reactive({ name: '', duration: 0, price: 0, category: '' });

const toggleEdit = (tempId: string) => {
  if (editingId.value === tempId) {
    editingId.value = null;
    return;
  }
  const service = props.services.find(s => s.tempId === tempId);
  if (!service) return;
  editForm.name = service.name;
  editForm.duration = service.duration;
  editForm.price = service.price;
  editForm.category = service.category ?? '';
  editingId.value = tempId;
};

const saveEdit = (tempId: string) => {
  emit('update-service', tempId, {
    name: editForm.name,
    duration: editForm.duration,
    price: editForm.price,
    category: editForm.category?.trim() || undefined,
  });
  editingId.value = null;
};

const openImagePicker = (service: IOnboardingSuggestedService) => {
  // Prefer the (possibly edited) name currently in the form for the search.
  const name = editingId.value === service.tempId ? editForm.name : service.name;
  openDrawer(
    ServiceImagePicker,
    {
      serviceName: name,
      onSelect: (imageUrl: string) => emit('set-image', service.tempId, imageUrl),
      onClose: closeDrawer,
    },
    { title: 'תמונות מוצעות' }
  );
};
</script>
